/**
 * Small, dependency-free inline SVG icons used across the dashboard panels.
 * Kept together so no icon library needs to be added to the project.
 */
import { SVGProps } from "react";

type IconProps = SVGProps<SVGSVGElement>;

export const BuildingIcon = (props: IconProps) => (
	<svg width="22" height="22" viewBox="0 0 24 24" fill="none" {...props}>
		<path d="M4 21V7l8-4 8 4v14" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
		<path d="M9 21v-6h6v6" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
	</svg>
);

export const CalendarIcon = (props: IconProps) => (
	<svg width="15" height="15" viewBox="0 0 24 24" fill="none" {...props}>
		<rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.8" />
		<path d="M3 10h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
	</svg>
);

export const WalletIcon = (props: IconProps) => (
	<svg width="15" height="15" viewBox="0 0 24 24" fill="none" {...props}>
		<path d="M4 21V7l8-4 8 4v14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
		<path d="M9 21v-6h6v6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
	</svg>
);

export const BellIcon = (props: IconProps) => (
	<svg width="15" height="15" viewBox="0 0 24 24" fill="none" {...props}>
		<path
			d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"
			stroke="currentColor"
			strokeWidth="1.8"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
		<path d="M13.7 21a2 2 0 0 1-3.4 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
	</svg>
);

export const PayrollTagIcon = (props: IconProps) => (
	<svg width="17" height="17" viewBox="0 0 24 24" fill="none" {...props}>
		<path
			d="M12 2v20M17 6.5c0-1.9-2-3-5-3s-5 1.3-5 3 2 2.6 5 3 5 1.1 5 3-2 3-5 3-5-1.1-5-3"
			stroke="currentColor"
			strokeWidth="1.8"
			strokeLinecap="round"
		/>
	</svg>
);

export const TaxIcon = (props: IconProps) => (
	<svg width="17" height="17" viewBox="0 0 24 24" fill="none" {...props}>
		<path d="M4 21V7l8-4 8 4v14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
		<path d="M9 21v-6h6v6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
	</svg>
);

export const DepositIcon = (props: IconProps) => (
	<svg width="17" height="17" viewBox="0 0 24 24" fill="none" {...props}>
		<rect x="3" y="6" width="18" height="13" rx="2" stroke="currentColor" strokeWidth="1.8" />
		<path d="M3 10h18" stroke="currentColor" strokeWidth="1.8" />
		<path d="M7 14h5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
	</svg>
);

export const RoeIcon = (props: IconProps) => (
	<svg width="17" height="17" viewBox="0 0 24 24" fill="none" {...props}>
		<path
			d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5z"
			stroke="currentColor"
			strokeWidth="1.8"
			strokeLinejoin="round"
		/>
		<path d="M14 3v5h5" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
	</svg>
);

export const YearEndIcon = (props: IconProps) => (
	<svg width="17" height="17" viewBox="0 0 24 24" fill="none" {...props}>
		<rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.8" />
		<path d="M3 10h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
	</svg>
);

export const CheckCircleIcon = (props: IconProps) => (
	<svg width="16" height="16" viewBox="0 0 24 24" fill="none" {...props}>
		<circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
		<path d="M8.5 12.3l2.3 2.3 4.7-4.9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
	</svg>
);

export const ClockIcon = (props: IconProps) => (
	<svg width="16" height="16" viewBox="0 0 24 24" fill="none" {...props}>
		<circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
		<path d="M12 7v6l4 2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
	</svg>
);

export const WarningIcon = (props: IconProps) => (
	<svg width="16" height="16" viewBox="0 0 24 24" fill="none" {...props}>
		<path d="M12 3l10 18H2L12 3z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
		<path d="M12 10v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
		<circle cx="12" cy="17" r="0.9" fill="currentColor" />
	</svg>
);

// ===== NEW: circular alert icon (used for banners/error cards instead of
// the triangle WarningIcon above) =====
export const AlertCircleIcon = (props: IconProps) => (
	<svg width="16" height="16" viewBox="0 0 24 24" fill="none" {...props}>
		<circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
		<path d="M12 8v5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
		<circle cx="12" cy="16" r="1" fill="currentColor" />
	</svg>
);

// ===== NEW: small trend arrows for the Account summary ledger/YTD rows =====
export const TrendDownIcon = (props: IconProps) => (
	<svg width="12" height="12" viewBox="0 0 24 24" fill="none" {...props}>
		<path d="M6 6l12 12M18 9v9h-9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
	</svg>
);
export const TrendCurlUpIcon = (props: IconProps) => (
	<svg width="12" height="12" viewBox="0 0 24 24" fill="none" {...props}>
		<path d="M23 6l-9.5 9.5-5-5L1 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
		<path d="M17 6h6v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
	</svg>
);

export const TrendUpIcon = (props: IconProps) => (
	<svg width="12" height="12" viewBox="0 0 24 24" fill="none" {...props}>
		<path d="M6 18L18 6M9 6h9v9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
	</svg>
);

export const HelpCircleIcon = (props: IconProps) => (
	<svg width="16" height="16" viewBox="0 0 24 24" fill="none" {...props}>
		<circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
		<path
			d="M9.5 9.3a2.6 2.6 0 1 1 3.6 2.4c-.8.4-1.1 1-1.1 1.8"
			stroke="currentColor"
			strokeWidth="1.8"
			strokeLinecap="round"
		/>
		<circle cx="12" cy="17" r="0.9" fill="currentColor" />
	</svg>
);

export const NewsIcon = (props: IconProps) => (
	<svg width="16" height="16" viewBox="0 0 24 24" fill="none" {...props}>
		<rect x="3" y="4" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.8" />
		<path d="M3 9h18" stroke="currentColor" strokeWidth="1.8" />
	</svg>
);

export const ChevronRightIcon = (props: IconProps) => (
	<svg width="13" height="13" viewBox="0 0 24 24" fill="none" {...props}>
		<path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
	</svg>
);

export const ChatIcon = (props: IconProps) => (
	<svg width="24" height="24" viewBox="0 0 24 24" fill="none" {...props}>
		<path
			d="M21 11.5a8.5 8.5 0 0 1-8.5 8.5H4l2.4-3.6A8.5 8.5 0 1 1 21 11.5z"
			stroke="currentColor"
			strokeWidth="1.8"
			strokeLinejoin="round"
		/>
	</svg>
);

// ===== NEW: icons for the Guides & help resource list (copy this block) =====
export const PlayCircleIcon = (props: IconProps) => (
	<svg width="16" height="16" viewBox="0 0 24 24" fill="none" {...props}>
		<circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
		<path d="M10 8.5l6 3.5-6 3.5v-7z" fill="currentColor" />
	</svg>
);

export const DocumentIcon = (props: IconProps) => (
	<svg width="16" height="16" viewBox="0 0 24 24" fill="none" {...props}>
		<path
			d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5z"
			stroke="currentColor"
			strokeWidth="1.8"
			strokeLinejoin="round"
		/>
		<path d="M14 3v5h5" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
		<path d="M8.5 13h7M8.5 16.5h5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
	</svg>
);

export const ExternalLinkIcon = (props: IconProps) => (
	<svg width="13" height="13" viewBox="0 0 24 24" fill="none" {...props}>
		<path d="M7 17L17 7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
		<path d="M9 7h8v8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
	</svg>
);